import React, { useState, useEffect, useCallback } from 'react';
import { useMeeting, useParticipant } from '@videosdk.live/react-sdk';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import SimpleAudioWave from './SimpleAudioWave';
import JoiningMeeting from './JoiningMeeting';
import useCallTimer from '../hooks/useCallTimer';
import {
  validateNewParticipant,
  selectOneToOneParticipants,
} from '../utils/participantManager';
import useCallManager from '../hooks/useCallManager';
import { useSocket } from '../../../../utils/Socket.Provider';
import { toast } from 'react-toastify';
import { IncomingCallDuringCallNotification } from './IncommingWatinCall';

const MeetingView = ({ onMeetingLeave, onParticipantNameChange }) => {
  const [joined, setJoined] = useState(null);
  const [activeParticipantId, setActiveParticipantId] = useState(null);
  const { endCall } = useCallManager();
  const [localParticipantId, setLocalParticipantId] = useState(null);
  const [timerStarted, setTimerStarted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { join, leave, toggleMic, end, localParticipant, localMicOn } =
    useMeeting();
  const { callDuration, formatDuration, startCallTimer, stopCallTimer } =
    useCallTimer();
  const [isLeavingCall, setIsLeavingCall] = useState(false);
  const [showTooManyParticipantsWarning, setShowTooManyParticipantsWarning] =
    useState(false);
  const [participantCount, setParticipantCount] = useState(0);

  // Incoming call during active call states
  const [incomingCallsQueue, setIncomingCallsQueue] = useState([]);
  const [showIncomingCallDuringCall, setShowIncomingCallDuringCall] =
    useState(false);
  const [currentIncomingCall, setCurrentIncomingCall] = useState(null);
  const { socket } = useSocket();

  // Meeting hooks
  const { participants } = useMeeting({
    onMeetingJoined: () => {
      try {
        setJoined('JOINED');
        // Store local participant ID when joined
        if (localParticipant) {
          setLocalParticipantId(localParticipant.id);
          console.log('Local participant ID set:', localParticipant.id);
        }
      } catch (error) {
        console.error('Error in onMeetingJoined:', error);
        handleFatalError();
      }
    },
    onMeetingLeft: () => {
      if (onMeetingLeave) onMeetingLeave();
    },
    onParticipantJoined: (participantId) => {
      try {
        console.log('Participant joined:', participantId);

        // Validate if this participant should be allowed in a one-to-one call
        const validation = validateNewParticipant(
          participants,
          participantId,
          localParticipantId
        );

        if (!validation.allowed) {
          console.warn(
            `Participant validation failed: ${validation.reason}`,
            validation.message
          );

          // If this is not the local participant, we should handle accordingly
          if (participantId !== localParticipantId) {
            console.log('Handling invalid participant:', participantId);

            // In a real implementation with the VideoSDK, you would use:
            // meeting.removeParticipant(participantId);

            // For now, we'll just not update the UI with this participant
            // and log the issue for debugging
            if (validation.reason === 'DUPLICATE_NAME') {
              console.warn(
                `Duplicate participant detected with name: ${
                  participants.get(participantId)?.displayName
                }`
              );

              // Show warning to user about duplicate participants
              setShowTooManyParticipantsWarning(true);
              setParticipantCount(participants.size);

              // Auto-hide warning after 10 seconds
              setTimeout(() => {
                setShowTooManyParticipantsWarning(false);
              }, 10000);
            }

            // We still update the active participant to ensure UI is correct
            updateActiveParticipant();
            return;
          }
        }

        // Always update the active participant when someone joins
        updateActiveParticipant();

        // Check if we now have 2 participants (both users joined)
        setTimeout(() => {
          if (participants.size === 2 && !timerStarted) {
            console.log('Both participants joined, starting timer');
            startCallTimer();
            setTimerStarted(true);
          }
        }, 500); // Small delay to ensure participants list is updated
      } catch (error) {
        console.error('Error in onParticipantJoined:', error);
        handleFatalError();
      }
    },
    // Track local mic state changes
    onMicStateChanged: (micEnabled) => {
      console.log('Local mic state changed:', micEnabled);
    },
    onError: (error) => {
      console.error('Meeting error:', error);
      handleFatalError();
    },
  });
  const isLocalPresentInParticipants = participants.has(localParticipant?.id);

  // Always call useParticipant hook, but with a fallback ID if needed
  const participantId = activeParticipantId || '';
  const activeParticipant = useParticipant(participantId);
  // Only extract properties if we have a valid participant
  const micOn =
    activeParticipantId && activeParticipant ? activeParticipant.micOn : false;
  const displayName =
    activeParticipantId && activeParticipant
      ? activeParticipant.displayName
      : '';
  // Handle isLocal property - different SDKs might use different property names
  const isLocal =
    activeParticipantId && activeParticipant
      ? activeParticipant.isLocal !== undefined
        ? activeParticipant.isLocal
        : activeParticipant.local
      : false;

  // Audio reference for the active participant
  const micRef = React.useRef(null);
  const [micPermissionState, setMicPermissionState] = useState('granted');
  const [audioContextState, setAudioContextState] = useState('running');

  // Monitor microphone permissions and audio context
  useEffect(() => {
    const checkMicPermissions = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'microphone',
        });
        setMicPermissionState(permissionStatus.state);

        permissionStatus.addEventListener('change', () => {
          console.log('Microphone permission changed:', permissionStatus.state);
          setMicPermissionState(permissionStatus.state);

          if (permissionStatus.state === 'denied') {
            console.warn('Microphone permission denied during call');
            toast.error(
              'Microphone access was denied. Please enable it in browser settings.'
            );
          }
        });
      } catch (error) {
        console.error('Error checking microphone permissions:', error);
      }
    };

    const checkAudioContext = () => {
      if (window.AudioContext || window.webkitAudioContext) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();

        setAudioContextState(audioContext.state);

        audioContext.addEventListener('statechange', () => {
          console.log('Audio context state changed:', audioContext.state);
          setAudioContextState(audioContext.state);

          if (audioContext.state === 'suspended') {
            console.warn('Audio context suspended during call');
            // Try to resume audio context
            audioContext
              .resume()
              .then(() => {
                console.log('Audio context resumed');
              })
              .catch((err) => {
                console.error('Failed to resume audio context:', err);
              });
          }
        });
      }
    };

    checkMicPermissions();
    checkAudioContext();
  }, []);

  // Handle mic stream changes for audio playback
  useEffect(() => {
    if (
      isLeavingCall ||
      !activeParticipantId ||
      !activeParticipant ||
      !micRef.current
    ) {
      return;
    }

    const micStream = activeParticipant.micStream;
    const isLocalParticipant =
      activeParticipant.isLocal ?? activeParticipant.local;

    if (micOn && micStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(micStream.track);

      micRef.current.srcObject = mediaStream;
      micRef.current.muted = isLocalParticipant; // Prevent echo for local participant

      if (!isLocalParticipant) {
        micRef.current.volume = 1.0;
      }

      // Play audio with fallback for user interaction requirement
      micRef.current.play().catch(() => {
        const playOnInteraction = () => {
          if (micRef.current) {
            micRef.current.play().catch(() => {});
            if (!isLocalParticipant) {
              micRef.current.muted = false;
              micRef.current.volume = 1.0;
            }
          }
          document.removeEventListener('click', playOnInteraction);
        };
        document.addEventListener('click', playOnInteraction);
      });
    } else {
      micRef.current.srcObject = null;
    }

    // Cleanup function
    return () => {
      if (micRef.current?.srcObject) {
        const tracks = micRef.current.srcObject.getTracks();
        tracks.forEach((track) => track.stop());
        micRef.current.srcObject = null;
      }
    };
  }, [activeParticipantId, activeParticipant, micOn, isLeavingCall]);

  const joinMeeting = useCallback(() => {
    setJoined('JOINING');
    join();
  }, [join]);

  useEffect(() => {
    const clearTimeOut = setTimeout(() => {
      if (participants.size === 0) {
        joinMeeting();
      }
    }, 2000);
    return () => {
      clearTimeout(clearTimeOut);
    };
  }, [participants.size, joinMeeting]);

  // Handle fatal errors by cleaning up and reloading
  const handleFatalError = useCallback(() => {
    setHasError(true);
    console.log('Handling fatal error - will clean up and reload');

    // Stop the timer if it's running
    stopCallTimer();

    // Clean up audio
    if (micRef.current) {
      try {
        micRef.current.pause();
        if (micRef.current.srcObject) {
          const tracks = micRef.current.srcObject.getTracks();
          tracks.forEach((track) => track.stop());
          micRef.current.srcObject = null;
        }
      } catch (err) {
        console.error('Error cleaning up audio:', err);
      }
    }

    // Try to leave the meeting
    try {
      if (end) end();
      if (leave) leave();
    } catch (err) {
      console.error('Error leaving meeting:', err);
    }

    // Reload the page after a short delay
    setTimeout(() => {
      if (onMeetingLeave) {
        onMeetingLeave();
      } else {
        window.location.reload();
      }
    }, 1000);
  }, [end, leave, onMeetingLeave, stopCallTimer]);

  // Function to find and set the remote participant
  const updateActiveParticipant = useCallback(() => {
    try {
      if (participants.size > 0) {
        // First try to find a remote participant (not yourself)
        const participantsList = [...participants.keys()];

        // Store local participant ID if not already set
        if (!localParticipantId && localParticipant) {
          setLocalParticipantId(localParticipant.id);
          console.log('Local participant ID set:', localParticipant.id);
        }

        // Find all participants
        const allParticipants = participantsList.map((id) => {
          const participant = participants.get(id);
          // Check if this is the local participant
          const isLocalParticipant =
            id === localParticipantId ||
            (localParticipant && id === localParticipant.id);

          return {
            id,
            isLocal: isLocalParticipant,
            displayName: participant.displayName || '',
          };
        });

        console.log('All participants:', allParticipants);

        // Enforce one-to-one call limit (max 2 participants)
        if (allParticipants.length > 2) {
          console.warn(
            'More than 2 participants detected in a one-to-one call'
          );

          // Use our utility to select the appropriate participants for a one-to-one call
          const {
            localParticipant: selectedLocalParticipant,
            remoteParticipant: selectedRemoteParticipant,
            duplicateParticipants,
          } = selectOneToOneParticipants(allParticipants, localParticipantId);

          if (duplicateParticipants > 0) {
            console.warn(
              `Found ${duplicateParticipants} duplicate participants that will be ignored`
            );

            // Show warning to user about too many participants
            setShowTooManyParticipantsWarning(true);
            setParticipantCount(allParticipants.length);

            // Auto-hide warning after 10 seconds
            setTimeout(() => {
              setShowTooManyParticipantsWarning(false);
            }, 10000);
          }

          // Only consider these two participants for the UI
          if (selectedRemoteParticipant) {
            console.log(
              'Selected remote participant:',
              selectedRemoteParticipant.id
            );
            setActiveParticipantId(selectedRemoteParticipant.id);

            // Pass the remote participant name to parent component
            if (
              onParticipantNameChange &&
              selectedRemoteParticipant.displayName
            ) {
              onParticipantNameChange(selectedRemoteParticipant.displayName);
            }

            // Start timer if not already started
            if (!timerStarted) {
              console.log('Both participants detected, starting timer');
              startCallTimer();
              setTimerStarted(true);
            }

            return;
          }
        }

        // Check if we have both participants and should start the timer
        if (allParticipants.length === 2 && !timerStarted) {
          console.log('Both participants detected, starting timer');
          startCallTimer();
          setTimerStarted(true);
        }

        // Find the first remote participant
        const remoteParticipant = allParticipants.find((p) => !p.isLocal);

        if (remoteParticipant) {
          console.log(
            'Setting remote participant as active:',
            remoteParticipant.id
          );
          setActiveParticipantId(remoteParticipant.id);

          // Pass the remote participant name to parent component if available
          if (onParticipantNameChange && remoteParticipant.displayName) {
            onParticipantNameChange(remoteParticipant.displayName);
          }
        } else {
          // If no remote participant, use the first one (likely yourself)
          console.log('No remote participant found, using first participant');
          setActiveParticipantId(participantsList[0]);
        }
      }
    } catch (error) {
      console.error('Error in updateActiveParticipant:', error);
      handleFatalError();
    }
  }, [
    participants,
    localParticipantId,
    localParticipant,
    onParticipantNameChange,
    timerStarted,
    startCallTimer,
    handleFatalError,
  ]);

  // Update active participant when participants change
  useEffect(() => {
    updateActiveParticipant();
  }, [participants, updateActiveParticipant]);

  // Update local participant ID when it becomes available
  useEffect(() => {
    if (localParticipant && !localParticipantId) {
      setLocalParticipantId(localParticipant.id);
      console.log('Local participant ID set from effect:', localParticipant.id);
    }
  }, [localParticipant, localParticipantId]);

  // Handle incoming calls during active call
  useEffect(() => {
    if (!socket) return;

    const handleIncomingCallDuringCall = (data) => {
      console.log('Incoming call during active call:', data);

      // Add to incoming calls queue
      const callWithId = {
        ...data,
        id: Date.now() + Math.random(),
        timestamp: new Date(),
      };

      setIncomingCallsQueue((prev) => [...prev, callWithId]);

      // Show notification if not already showing one
      if (!showIncomingCallDuringCall) {
        setCurrentIncomingCall(callWithId);
        setShowIncomingCallDuringCall(true);
      } else {
        // Show toast for additional calls
        toast.info(
          `Incoming call from ${data.name || 'Unknown'} (${
            incomingCallsQueue.length + 1
          } waiting)`,
          {
            position: 'top-right',
            autoClose: 3000,
          }
        );
      }
    };

    const handleCallCancelled = (data) => {
      console.log('Call cancelled during active call:', data);

      // Remove from queue if it exists
      setIncomingCallsQueue((prev) =>
        prev.filter((call) => call.meetingId !== data.meetingId)
      );

      // If this was the current showing call, show next one
      if (
        currentIncomingCall &&
        currentIncomingCall.meetingId === data.meetingId
      ) {
        setShowIncomingCallDuringCall(false);
        setCurrentIncomingCall(null);

        // Show next call if any
        setTimeout(() => {
          setIncomingCallsQueue((prev) => {
            if (prev.length > 0) {
              const nextCall = prev[0];
              setCurrentIncomingCall(nextCall);
              setShowIncomingCallDuringCall(true);
              return prev.slice(1);
            }
            return prev;
          });
        }, 300);
      }
    };
    const handleRemoteEndCall = () => {
      console.log('ending meeting sdk');
      leave();
      end();
    };

    socket.on('missed-call', handleIncomingCallDuringCall);
    socket.on('call-cancelled', handleCallCancelled);
    socket.on('end-call', handleRemoteEndCall);

    return () => {
      socket.off('missed-call', handleIncomingCallDuringCall);
      socket.off('call-cancelled', handleCallCancelled);
    };
  }, [
    socket,
    showIncomingCallDuringCall,
    currentIncomingCall,
    incomingCallsQueue.length,
    leave,
    end,
  ]);

  // Global cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up audio when component unmounts
      if (micRef.current) {
        try {
          // Pause the audio element first
          micRef?.current?.pause();

          // Stop all tracks in the stream if it exists
          if (micRef.current.srcObject) {
            const tracks = micRef.current.srcObject.getTracks();
            tracks.forEach((track) => {
              track.stop();
            });

            // Clear the srcObject
            micRef.current.srcObject = null;
          }
        } catch (error) {
          console.error('Error cleaning up audio on unmount:', error);
        }
      }
    };
  }, []);

  // Monitor page visibility to detect tab switching which can affect audio
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('Tab became hidden - audio might be affected');
      } else {
        console.log('Tab became visible - checking audio state');

        // Small delay to allow browser to restore audio context
        setTimeout(() => {
          if (joined === 'JOINED' && localMicOn) {
            // Re-check microphone permissions when tab becomes visible
            navigator.permissions
              .query({ name: 'microphone' })
              .then((result) => {
                if (result.state === 'denied') {
                  toast.error(
                    'Microphone permission was denied while tab was hidden'
                  );
                }
              })
              .catch((err) => console.log('Permission check failed:', err));
          }
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [joined, localMicOn]);

  // Handle ending the call
  const handleEndCall = () => {
    setIsLeavingCall(true);

    // Clean up audio before ending the call
    if (micRef.current) {
      try {
        // Pause the audio element first
        micRef.current.pause();

        // Stop all tracks in the stream if it exists
        if (micRef.current.srcObject) {
          const tracks = micRef.current.srcObject.getTracks();
          tracks.forEach((track) => {
            track.stop();
          });

          // Clear the srcObject
          micRef.current.srcObject = null;
        }

        console.log('Audio cleaned up before ending call');
      } catch (error) {
        console.error('Error cleaning up audio before ending call:', error);
      }
    }
    // Small delay to ensure audio is cleaned up before ending the call

    if (end) end();
    if (leave) leave();
    if (onMeetingLeave) onMeetingLeave(callDuration);
    endCall();
  };

  // Handle declining incoming call during active call
  const handleDeclineIncomingCallDuringCall = () => {
    if (currentIncomingCall && socket) {
      // Send decline signal to caller
      socket.emit('endcall', {
        toUser: currentIncomingCall.fromUser,
        fromUser: localParticipantId,
        meetingId: currentIncomingCall.meetingId,
      });

      // Remove from queue and show next if any
      setIncomingCallsQueue((prev) =>
        prev.filter((call) => call.id !== currentIncomingCall.id)
      );
      setShowIncomingCallDuringCall(false);
      setCurrentIncomingCall(null);

      // Show next incoming call if any
      setTimeout(() => {
        if (incomingCallsQueue.length > 1) {
          const nextCall = incomingCallsQueue.find(
            (call) => call.id !== currentIncomingCall.id
          );
          if (nextCall) {
            setCurrentIncomingCall(nextCall);
            setShowIncomingCallDuringCall(true);
          }
        }
      }, 300);
    }
  };

  // Microphone health check function
  const checkMicrophoneHealth = useCallback(async () => {
    try {
      // Check if we have access to microphone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const tracks = stream.getTracks();

      if (tracks.length === 0) {
        console.warn('No audio tracks available');
        toast.warning('No microphone detected. Please check your device.');
        return false;
      }

      const audioTrack = tracks[0];

      // Check track state
      if (audioTrack.readyState === 'ended') {
        console.warn('Microphone track is ended');
        toast.error('Microphone connection lost. Please refresh and rejoin.');
        return false;
      }

      if (!audioTrack.enabled) {
        console.warn('Microphone track is disabled');
        toast.warning('Microphone is disabled. Please enable it.');
        return false;
      }

      // Clean up test stream
      tracks.forEach((track) => track.stop());

      console.log('Microphone health check passed');
      return true;
    } catch (error) {
      console.error('Microphone health check failed:', error);

      if (error.name === 'NotAllowedError') {
        toast.error(
          'Microphone permission denied. Please allow access in browser settings.'
        );
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found. Please connect a microphone device.');
      } else {
        toast.error('Microphone error. Please check your device and refresh.');
      }

      return false;
    }
  }, []);

  // Periodic microphone health check during call
  useEffect(() => {
    if (joined === 'JOINED' && localMicOn) {
      const healthCheckInterval = setInterval(() => {
        checkMicrophoneHealth();
      }, 30000); // Check every 30 seconds

      return () => clearInterval(healthCheckInterval);
    }
  }, [joined, localMicOn, checkMicrophoneHealth]);

  // Handle accepting incoming call during active call (end current call and accept new one)
  const handleAcceptIncomingCallDuringCall = () => {
    if (currentIncomingCall && socket) {
      // First end the current call
      handleEndCall();

      // Then accept the new call after a short delay
      setTimeout(() => {
        socket.emit('accept-call', {
          toUser: currentIncomingCall.fromUser,
          fromUser: localParticipantId,
          meetingId: currentIncomingCall.meetingId,
        });

        // Clear states
        setIncomingCallsQueue([]);
        setShowIncomingCallDuringCall(false);
        setCurrentIncomingCall(null);

        // Navigate to new call
        window.location.href = `/oncall/${currentIncomingCall.fromUser}/${currentIncomingCall.meetingId}`;
      }, 1000);
    }
  };

  return (
    <div className='fixed inset-0 bg-gradient-to-b from-white to-gray-100 flex items-center justify-center z-[9999]'>
      {/* Hidden audio element for playback */}
      <audio ref={micRef} autoPlay playsInline />

      {/* Incoming Call During Active Call Notification */}
      {showIncomingCallDuringCall && currentIncomingCall && (
        <IncomingCallDuringCallNotification
          callerName={currentIncomingCall.name}
          callerImage={currentIncomingCall.profilePic}
          queueCount={incomingCallsQueue.length + 1}
          onAccept={handleAcceptIncomingCallDuringCall}
          onDecline={handleDeclineIncomingCallDuringCall}
        />
      )}

      {/* Background decorative elements */}
      <div className='absolute inset-0 overflow-hidden'>
        <div className='absolute top-0 left-1/4 w-64 h-64 rounded-full bg-[#A40165]/5 blur-3xl'></div>
        <div className='absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-[#A40165]/5 blur-3xl'></div>
        <div className='absolute top-1/3 right-1/3 w-40 h-40 rounded-full bg-purple-200/20 blur-2xl'></div>
      </div>

      <div className='w-full max-w-2xl mx-auto p-6 relative'>
        {hasError ? (
          <div className='bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden border border-gray-200 p-8 text-center'>
            <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-8 w-8 text-red-500'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <h3 className='text-xl font-medium text-gray-800 mb-2'>
              Connection Error
            </h3>
            <p className='text-gray-600 mb-6'>
              We encountered a problem with your call connection.
            </p>
            <p className='text-gray-500 text-sm mb-6'>
              The page will reload automatically to restore your connection.
            </p>
            <div className='animate-pulse'>
              <div className='h-2 w-24 bg-[#A40165]/30 rounded mx-auto'></div>
            </div>
          </div>
        ) : joined === 'JOINED' ? (
          <div className='bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden border border-gray-200'>
            {/* Call header */}
            <div className='bg-[#A40165] text-white py-4 px-6'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-5 w-5 mr-2'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path d='M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z' />
                  </svg>
                  <h2 className=' text-white font-bold'>Active Call</h2>
                </div>
                <div className='text-sm opacity-80'>
                  {formatDuration(callDuration)}
                </div>
              </div>
            </div>

            {/* Call content */}
            <div className='p-8'>
              {activeParticipantId ? (
                <div className='flex flex-col items-center'>
                  {/* Participant avatar with pulsating rings */}
                  <div className='relative mb-8 mt-8'>
                    {/* Pulsating rings - only show when mic is on */}
                    {micOn && (
                      <>
                        <div
                          className='absolute inset-0 rounded-full border-2 border-[#A40165]/30 animate-ping'
                          style={{
                            transform: 'scale(1.6)',
                            animationDuration: '2s',
                          }}
                        ></div>
                        <div
                          className='absolute inset-0 rounded-full border-2 border-[#A40165]/20 animate-ping'
                          style={{
                            transform: 'scale(1.4)',
                            animationDuration: '2.5s',
                            animationDelay: '0.2s',
                          }}
                        ></div>
                        <div
                          className='absolute inset-0 rounded-full border-2 border-[#A40165]/10 animate-ping'
                          style={{
                            transform: 'scale(1.2)',
                            animationDuration: '3s',
                            animationDelay: '0.4s',
                          }}
                        ></div>
                      </>
                    )}

                    {/* Participant avatar */}
                    <div
                      className={`w-32 h-32 rounded-full flex items-center justify-center shadow-lg
                      ${
                        isLocal
                          ? 'bg-gradient-to-br from-[#A40165]/20 to-[#A40165]/10'
                          : 'bg-gradient-to-br from-gray-200 to-white'
                      } 
                      border-4 ${
                        micOn ? 'border-[#A40165]' : 'border-gray-300'
                      }`}
                    >
                      <span
                        className={`text-4xl font-bold ${
                          isLocal ? 'text-[#A40165]' : 'text-gray-700'
                        }`}
                      >
                        {displayName?.charAt(0)?.toUpperCase() || 'U'}
                      </span>

                      {/* Mic status indicator */}
                      <div
                        className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-2 border-white flex items-center justify-center shadow-md
                        ${micOn ? 'bg-[#A40165]' : 'bg-gray-400'}`}
                      >
                        {micOn ? (
                          <FaMicrophone className='text-white text-sm' />
                        ) : (
                          <FaMicrophoneSlash className='text-white text-sm' />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Participant name */}
                  <div className='mb-6 text-center'>
                    <p className='text-xl font-medium text-gray-800'>
                      {displayName || (isLocal ? 'You' : 'Participant')}
                    </p>
                    <p className='text-[#A40165] text-sm font-medium mt-1'>
                      {isLocal ? 'You' : 'Connected'} •{' '}
                      {micOn ? 'Mic on' : 'Mic off'}
                    </p>
                  </div>

                  {/* Microphone status warnings */}
                  {micPermissionState === 'denied' && (
                    <div className='mb-4 px-4 py-2 bg-red-100 border border-red-300 rounded-full flex items-center justify-center'>
                      <p className='text-sm text-red-700 flex items-center justify-center'>
                        <svg
                          className='w-4 h-4 mr-2'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                        >
                          <path
                            fillRule='evenodd'
                            d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                            clipRule='evenodd'
                          />
                        </svg>
                        Microphone access denied
                      </p>
                    </div>
                  )}

                  {audioContextState === 'suspended' && (
                    <div className='mb-4 px-4 py-2 bg-yellow-100 border border-yellow-300 rounded-full flex items-center justify-center'>
                      <p className='text-sm text-yellow-700 flex items-center justify-center'>
                        <svg
                          className='w-4 h-4 mr-2'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                        >
                          <path
                            fillRule='evenodd'
                            d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                            clipRule='evenodd'
                          />
                        </svg>
                        Audio context suspended
                      </p>
                    </div>
                  )}

                  {/* Your mic status - only show if viewing remote participant */}
                  {!isLocal && (
                    <div className='mb-4 px-4 py-2 bg-gray-100/70 rounded-full flex items-center justify-center'>
                      <p className='text-sm text-gray-700 flex items-center justify-center'>
                        <span
                          className={`w-2 h-2 rounded-full mr-2 ${
                            localMicOn ? 'bg-green-500' : 'bg-red-500'
                          }`}
                        ></span>
                        Your microphone is {localMicOn ? 'on' : 'off'}
                      </p>
                    </div>
                  )}

                  {/* Audio wave visualization */}
                  <div className='mb-8 w-full flex justify-center'>
                    <div className='w-full max-w-md'>
                      <SimpleAudioWave
                        micRef={micRef}
                        isLocal={isLocal}
                        micOn={micOn}
                        isOnlyLocal={
                          participants.size === 1 &&
                          isLocalPresentInParticipants
                        }
                      />
                    </div>
                  </div>

                  {/* Call Controls */}
                  <div className='w-full max-w-md'>
                    {/* Call Controls */}
                    <div className='flex justify-center gap-8 items-center'>
                      {/* Additional Controls - can add more buttons here */}

                      {/* Toggle Mic Button - Always shows YOUR mic status */}
                      <button
                        onClick={async () => {
                          console.log('Toggling local mic:', !localMicOn);

                          // If turning mic on, check health first
                          if (!localMicOn) {
                            const isHealthy = await checkMicrophoneHealth();
                            if (!isHealthy) {
                              console.warn(
                                'Microphone health check failed, not toggling'
                              );
                              return;
                            }
                          }

                          try {
                            toggleMic();

                            // Additional verification after toggle
                            setTimeout(() => {
                              if (
                                localMicOn &&
                                micPermissionState !== 'granted'
                              ) {
                                toast.warning(
                                  'Microphone permission may have been revoked. Please check browser settings.'
                                );
                              }
                            }, 1000);
                          } catch (error) {
                            console.error('Error toggling microphone:', error);
                            toast.error(
                              'Failed to toggle microphone. Please try again.'
                            );
                          }
                        }}
                        className='group flex flex-col items-center'
                        disabled={isLeavingCall}
                      >
                        <div
                          className={`w-16 h-16 rounded-full flex items-center justify-center shadow-md transition-all duration-300 
                          ${
                            localMicOn
                              ? 'bg-[#A40165] hover:bg-[#8A0154]'
                              : 'bg-gray-600 hover:bg-gray-700'
                          }`}
                        >
                          {localMicOn ? (
                            <FaMicrophone className='text-white text-xl' />
                          ) : (
                            <FaMicrophoneSlash className='text-white text-xl' />
                          )}
                        </div>
                        <span className='text-gray-600 text-sm mt-2 group-hover:text-gray-800'>
                          {localMicOn ? 'Mute' : 'Unmute'}
                        </span>
                      </button>

                      {/* Leave Button */}
                      <button
                        onClick={handleEndCall}
                        className='group flex flex-col items-center'
                        disabled={isLeavingCall}
                      >
                        <div
                          className={`w-16 h-16 rounded-full flex items-center justify-center shadow-md transition-all duration-300
                          ${
                            isLeavingCall
                              ? 'bg-gray-400'
                              : 'bg-red-600 hover:bg-red-700'
                          }`}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-7 w-7 text-white'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M6 18L18 6M6 6l12 12'
                            />
                          </svg>
                        </div>
                        <span className='text-gray-600 text-sm mt-2 group-hover:text-gray-800'>
                          {isLeavingCall ? 'Ending...' : 'End Call'}
                        </span>
                      </button>
                    </div>

                    {/* Call ending message */}
                    {isLeavingCall && (
                      <div className='mt-8 text-center'>
                        <p className='text-[#A40165] animate-pulse'>
                          Ending call...
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className='text-center py-12'>
                  <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-8 w-8 text-gray-400'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                      />
                    </svg>
                  </div>
                  <p className='text-gray-600'>
                    Waiting for participants to join...
                  </p>
                  <p className='text-gray-400 text-sm mt-2'>
                    The call will begin when others connect
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <JoiningMeeting />
        )}
      </div>
    </div>
  );
};

export default MeetingView;

// useEffect(() => {
//   // Skip if we're leaving the call
//   if (isLeavingCall) {
//     return;
//   }

//   if (activeParticipantId && activeParticipant && micRef.current) {
//     const micStream = activeParticipant.micStream;

//     // Check if this is the local participant
//     const isLocalParticipant =
//       activeParticipant.isLocal !== undefined
//         ? activeParticipant.isLocal
//         : activeParticipant.local;

//     try {
//       if (micOn && micStream) {
//         // Create a new MediaStream with the audio track
//         const mediaStream = new MediaStream();
//         mediaStream.addTrack(micStream.track);

//         // Monitor track state for local participant
//         if (isLocalParticipant) {
//           const track = micStream.track;

//           // Monitor track state changes
//           track.addEventListener('ended', () => {
//             console.warn('Local microphone track ended unexpectedly');
//             toast.error(
//               'Microphone connection lost. Please check your device.'
//             );
//           });

//           track.addEventListener('mute', () => {
//             console.warn('Local microphone track muted');
//           });

//           track.addEventListener('unmute', () => {
//             console.log('Local microphone track unmuted');
//           });

//           // Check track state periodically
//           const trackMonitor = setInterval(() => {
//             if (track.readyState === 'ended') {
//               console.warn('Microphone track is in ended state');
//               clearInterval(trackMonitor);
//               toast.error(
//                 'Microphone disconnected. Please refresh and rejoin.'
//               );
//             } else if (!track.enabled) {
//               console.warn('Microphone track is disabled');
//             }
//           }, 5000);

//           // Cleanup monitor when component unmounts or track changes
//           return () => clearInterval(trackMonitor);
//         }

//         // Set the stream as source for the audio element
//         micRef.current.srcObject = mediaStream;

//         // IMPORTANT: Mute the audio if it's the local participant to prevent echo
//         // This is the key fix - make sure we're NOT muting remote participants' audio
//         micRef.current.muted = isLocalParticipant;

//         // Set volume to ensure it's audible (sometimes browsers reduce volume)
//         if (!isLocalParticipant) {
//           micRef.current.volume = 1.0;
//         }

//         // Play the audio - important for audio to work
//         const playPromise = micRef.current.play();

//         if (playPromise !== undefined) {
//           playPromise
//             .then(() => {
//               // Double check that remote audio is not muted
//               if (!isLocalParticipant) {
//                 micRef.current.muted = false;
//               }
//             })
//             .catch((error) => {
//               // Only handle the error if we're not leaving the call
//               if (!isLeavingCall && micRef.current) {
//                 console.error('Error playing audio:', error);
//                 // Try again with user interaction
//                 const playAudio = () => {
//                   if (micRef.current) {
//                     micRef.current
//                       .play()
//                       .then(() => {
//                         // Ensure remote audio is not muted after user interaction
//                         if (!isLocalParticipant) {
//                           micRef.current.muted = false;
//                           micRef.current.volume = 1.0;
//                           console.log('Audio resumed after user interaction');
//                         }
//                       })
//                       .catch((e) => console.log('Ignored play error:', e));
//                   }
//                   document.removeEventListener('click', playAudio);
//                 };
//                 document.addEventListener('click', playAudio);
//               }
//             });
//         }
//       } else {
//         // No mic stream or mic is off
//         if (micRef.current.srcObject) {
//           micRef.current.srcObject = null;
//         }
//       }
//     } catch (error) {
//       console.error('Error setting up audio:', error);
//     }
//   }

//   // Cleanup function to stop audio when dependencies change
//   return () => {
//     if (micRef.current && micRef.current.srcObject) {
//       try {
//         // Stop all tracks in the stream
//         const tracks = micRef.current.srcObject.getTracks();
//         tracks.forEach((track) => {
//           track.stop();
//         });

//         // Clear the srcObject
//         micRef.current.srcObject = null;
//       } catch (error) {
//         console.error('Error cleaning up audio stream:', error);
//       }
//     }
//   };
// }, [activeParticipantId, activeParticipant, micOn, isLeavingCall]);
